import React, { useState } from 'react';
import { Users, ExternalLink, Instagram, Linkedin, Twitter, Youtube, Facebook, Star, Mail } from 'lucide-react';
import JourneyHeader from './JourneyHeader';
import Footer from './Footer';

const TeacherReferrals = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const teachers = [
    {
      name: 'Dr. Maya Johnson',
      title: 'Spiritual Wellness & Mindfulness Coach',
      category: 'Spirituality',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=600&fit=crop',
      bio: 'Leading expert in holistic wellness practices with 15+ years of experience in spiritual guidance, meditation, and transformative healing. Dr. Maya has helped thousands discover their inner peace and purpose.',
      specialties: ['Meditation', 'Energy Healing', 'Life Coaching', 'Breathwork'],
      rating: 4.9,
      students: '2,500+',
      website: '#',
      social: {
        instagram: '#',
        linkedin: '#'
      }
    },
    {
      name: 'Marcus Williams',
      title: 'Entrepreneurship & Business Strategy',
      category: 'Business',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop',
      bio: 'Serial entrepreneur and business strategist helping purpose-driven leaders build sustainable, impactful businesses. Marcus specializes in conscious capitalism and ethical business practices.',
      specialties: ['Business Planning', 'Digital Strategy', 'Brand Building', 'Sales'],
      rating: 4.8,
      students: '1,800+',
      website: '#',
      social: {
        linkedin: '#',
        twitter: '#'
      }
    },
    {
      name: 'Sister Amara',
      title: 'Sacred Wisdom & Ancient Practices',
      category: 'Spirituality',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=600&fit=crop',
      bio: 'Keeper of ancestral knowledge, teaching ancient spiritual practices for modern transformation and enlightenment. Sister Amara bridges timeless wisdom with contemporary life.',
      specialties: ['Ancestral Wisdom', 'Ritual Practices', 'Spiritual Guidance', 'Sacred Ceremonies'],
      rating: 5.0,
      students: '3,200+',
      website: '#',
      social: {
        instagram: '#',
        youtube: '#'
      }
    },
    {
      name: 'David Chen',
      title: 'Financial Abundance & Wealth Mindset',
      category: 'Finance',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=600&fit=crop',
      bio: 'Certified financial coach specializing in wealth consciousness and abundance mindset for spiritual entrepreneurs. David helps clients break through money blocks and create prosperity.',
      specialties: ['Financial Planning', 'Wealth Mindset', 'Investment Strategy', 'Money Healing'],
      rating: 4.9,
      students: '1,500+',
      website: '#',
      social: {
        linkedin: '#',
        facebook: '#'
      }
    },
    {
      name: 'Rev. Sarah Thompson',
      title: 'Healing Arts & Transformational Therapy',
      category: 'Healing',
      image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600&h=600&fit=crop',
      bio: 'Licensed therapist and healing arts practitioner helping individuals overcome trauma and find their authentic path. Rev. Sarah combines psychology with spiritual healing modalities.',
      specialties: ['Trauma Healing', 'Art Therapy', 'Spiritual Counseling', 'Shadow Work'],
      rating: 4.9,
      students: '2,100+',
      website: '#',
      social: {
        instagram: '#',
        linkedin: '#'
      }
    },
    {
      name: 'Prof. James Martinez',
      title: 'Digital Transformation & Innovation',
      category: 'Technology',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=600&fit=crop',
      bio: 'Technology innovator and educator bridging ancient wisdom with cutting-edge digital strategies for impact. Prof. James helps spiritual teachers amplify their reach through technology.',
      specialties: ['Digital Strategy', 'Tech Innovation', 'Online Education', 'AI Integration'],
      rating: 4.7,
      students: '1,200+',
      website: '#',
      social: {
        linkedin: '#',
        twitter: '#'
      }
    }
  ];

  const categories = ['all', 'Spirituality', 'Business', 'Finance', 'Healing', 'Technology'];

  const filteredTeachers = selectedCategory === 'all' 
    ? teachers 
    : teachers.filter(teacher => teacher.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <JourneyHeader activeSection="referrals" />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <div className="mb-8">
            <Users size={64} className="mx-auto text-brand-deep mb-4" />
          </div>
          <h1 className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-brand-deep via-fire-main to-sacred-teal bg-clip-text text-transparent mb-6">
            Trusted Teachers
          </h1>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
            A curated directory of exceptional teachers, coaches, and mentors we know, like, and trust.
          </p>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Each brings unique wisdom and transformative guidance to your journey of growth and awakening.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white sticky top-24 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  selectedCategory === cat
                    ? 'bg-gradient-to-r from-brand-deep to-fire-main text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? 'All Teachers' : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Teachers Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTeachers.map((teacher, index) => (
              <div
                key={index}
                className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* Teacher Image */}
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={teacher.image}
                    alt={teacher.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-4 py-2 bg-white/90 backdrop-blur-sm text-brand-deep rounded-full text-sm font-bold">
                      {teacher.category}
                    </span>
                  </div>

                  {/* Rating */}
                  <div className="absolute top-4 right-4 flex items-center space-x-1 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-full">
                    <Star size={16} className="text-brand-gold fill-brand-gold" />
                    <span className="text-sm font-bold text-gray-900">{teacher.rating}</span>
                  </div>
                </div>

                {/* Teacher Info */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{teacher.name}</h3>
                  <p className="text-brand-deep font-semibold mb-3">{teacher.title}</p>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{teacher.bio}</p>

                  {/* Specialties */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {teacher.specialties.map((specialty, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 bg-gradient-to-r from-brand-deep/10 to-fire-main/10 text-gray-700 text-xs font-medium rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-gray-100">
                    <div>
                      <div className="text-lg font-bold text-gray-900">{teacher.students}</div>
                      <div className="text-xs text-gray-500">Students</div>
                    </div>
                    <div className="w-px h-8 bg-gray-200"></div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">{teacher.rating}/5</div>
                      <div className="text-xs text-gray-500">Rating</div>
                    </div>
                  </div>

                  {/* Social Links & Website */}
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-3">
                      {teacher.social.instagram && (
                        <a href={teacher.social.instagram} className="text-gray-400 hover:text-brand-deep transition-colors">
                          <Instagram size={20} />
                        </a>
                      )}
                      {teacher.social.linkedin && (
                        <a href={teacher.social.linkedin} className="text-gray-400 hover:text-brand-deep transition-colors">
                          <Linkedin size={20} />
                        </a>
                      )}
                      {teacher.social.twitter && (
                        <a href={teacher.social.twitter} className="text-gray-400 hover:text-brand-deep transition-colors">
                          <Twitter size={20} />
                        </a>
                      )}
                      {teacher.social.youtube && (
                        <a href={teacher.social.youtube} className="text-gray-400 hover:text-brand-deep transition-colors">
                          <Youtube size={20} />
                        </a>
                      )}
                      {teacher.social.facebook && (
                        <a href={teacher.social.facebook} className="text-gray-400 hover:text-brand-deep transition-colors">
                          <Facebook size={20} />
                        </a>
                      )}
                    </div>
                    <a 
                      href={teacher.website}
                      className="flex items-center space-x-1 text-brand-deep font-semibold text-sm hover:text-fire-main transition-colors group/link"
                    >
                      <span>Visit</span>
                      <ExternalLink size={16} className="group-hover/link:translate-x-0.5 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Become Featured CTA */}
      <section className="py-16 bg-gradient-to-r from-brand-deep to-fire-main">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center text-white">
          <Mail size={64} className="mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">Are You a Teacher or Coach?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            If you're making an impact in your community and align with our mission, we'd love to feature you in our trusted directory.
          </p>
          <button className="px-12 py-4 bg-white text-brand-deep font-bold rounded-full hover:bg-brand-gold hover:text-white transition-all duration-300 hover:shadow-2xl text-lg">
            Apply to Be Featured
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeacherReferrals;